import { useCallback, useEffect, useState } from 'react'
import clsx from 'clsx'
import ReactDOM from 'react-dom'

import styles from './bottom-sheet.module.scss'

import { Button, Icon, ModalOverlay } from '..'

interface IBottomSheetProps {
    children: React.ReactNode
    closeHandlerReady?: boolean
    onClose: () => void
    title?: string
    withoutHeader?: boolean
}

const getKeyboardOffsetPx = (): number => {
    const visualViewport = window.visualViewport

    if (!visualViewport) {
        return 0
    }

    return Math.max(0, window.innerHeight - visualViewport.height)
}

export const BottomSheet = ({
    withoutHeader,
    onClose,
    closeHandlerReady,
    title,
    children,
}: IBottomSheetProps): React.ReactNode => {
    const [isClosingLocal, setIsClosingLocal] = useState(false)
    const [keyboardOffsetPx, setKeyboardOffsetPx] = useState(getKeyboardOffsetPx)
    const [modalRoot] = useState(() => document.querySelector('#modal-root'))
    const isClosing = isClosingLocal || Boolean(closeHandlerReady)

    const handleClose = useCallback(() => {
        setIsClosingLocal(true)
        setTimeout(() => {
            onClose()
        }, 300)
    }, [onClose])

    useEffect(() => {
        if (!closeHandlerReady) {
            return
        }

        const timer = setTimeout(() => {
            onClose()
        }, 300)

        return () => {
            clearTimeout(timer)
        }
    }, [closeHandlerReady, onClose])

    useEffect(() => {
        const visualViewport = window.visualViewport

        if (!visualViewport) {
            return
        }

        const update = (): void => {
            setKeyboardOffsetPx(getKeyboardOffsetPx())
        }

        visualViewport.addEventListener('resize', update)
        visualViewport.addEventListener('scroll', update)

        return () => {
            visualViewport.removeEventListener('resize', update)
            visualViewport.removeEventListener('scroll', update)
        }
    }, [])

    if (!modalRoot) {
        return null
    }

    return ReactDOM.createPortal(
        <>
            <div
                style={{ marginBottom: keyboardOffsetPx ? `${keyboardOffsetPx}px` : undefined }}
                className={clsx(
                    styles['bottom-sheet__modal'],
                    isClosing && styles['bottom-sheet__modal_closing'],
                )}
            >
                {!withoutHeader && (
                    <div className={styles['bottom-sheet__header']}>
                        <div className={styles['bottom-sheet__header-back']} />{' '}
                        {/* Мб здесь появится кнопка назад */}
                        <h1 className={styles['bottom-sheet__header-title']}>{title}</h1>
                        <div className={styles['bottom-sheet__header-close']}>
                            <Button size='icon' variant='secondary' onClick={handleClose}>
                                <Icon icon='x' size={20} />
                            </Button>
                        </div>
                    </div>
                )}

                {children}
            </div>
            <ModalOverlay isClosing={isClosing} onClose={handleClose} />
        </>,
        modalRoot,
    )
}
